import React, { Component, Fragment, useEffect } from 'react';
import { connect } from 'react-redux';

import * as analytics from '../../../utilities/analytics';
import * as sentry from '../../../utilities/sentry';
import LoggedOutScreen from './logged-out-screen';
import SendEmailVerificationEmail from './SendEmailVerification.graphql';
import {
  EMBED_NEED_EMAIL_VERIFICATION_SCREEN_VIEWED,
  EMBED_NOT_IN_EMAIL_SCREEN_VIEWED,
} from '../../../constants/events';
import { LOOM_URI } from '../../../constants/routes';
import { getGraphQLClient } from '../../../utilities/graphql';
import { APP_SOURCE_SLACK_DESKTOP } from '../../../../../shared/constants/analytics';
import { STATUS_FORBIDDEN } from '../../../../../shared/constants/http';
import { VERIFIED } from '../../../../../shared/constants/userStatus';

import Button from 'components/ui-components/button';
import Modal from 'components/ui-components/modal';

import './styles.less';

const ViewerNotInEmailWhitelistScreen = ({
  error,
  onClick,
  requestSent,
  alreadyPinged,
  isFromSlackDesktopApp,
}) => {
  useEffect(() => {
    const props = {};

    if (isFromSlackDesktopApp) {
      props.source = APP_SOURCE_SLACK_DESKTOP;
    }

    analytics.track(EMBED_NOT_IN_EMAIL_SCREEN_VIEWED, props);
  }, [isFromSlackDesktopApp]);

  return (
    <Fragment>
      {!requestSent && (
        <Fragment>
          <div className="loom-permissions-modal-title">
            You need permission to view this video
          </div>
          <div className="loom-permissions-modal-body">
            Public viewing has been disabled by the video owner.
          </div>
          <Button
            onClicked={onClick}
            capitalize={false}
            buttonType="rp-primary-2"
            value="Request access"
          />
        </Fragment>
      )}
      {requestSent && (
        <div className="loom-permissions-modal-title">
          {!alreadyPinged && 'Your request has been sent!'}
          {alreadyPinged &&
            'You’re only allowed to request access once a day per video.'}
        </div>
      )}
      {error && <div className="loom-permissions-modal-title">{error}</div>}
    </Fragment>
  );
};

const ViewerNeedsEmailVerificationScreen = ({
  email,
  error,
  onClick,
  requestSent,
  isFromSlackDesktopApp,
}) => {
  useEffect(() => {
    const props = {};

    if (isFromSlackDesktopApp) {
      props.source = APP_SOURCE_SLACK_DESKTOP;
    }

    analytics.track(EMBED_NEED_EMAIL_VERIFICATION_SCREEN_VIEWED, props);
  }, [isFromSlackDesktopApp]);

  return (
    <Fragment>
      {!requestSent && (
        <Fragment>
          <div className="loom-permissions-modal-title">
            You need permission to view this video
          </div>
          <div className="loom-permissions-modal-body">
            Public viewing has been disabled by the video owner. Verify your
            email ({email}) to view.
          </div>
          <Button
            onClicked={onClick}
            capitalize={false}
            buttonType="rp-primary-2"
            value="Resend Verify Email"
          />
        </Fragment>
      )}
      {requestSent && (
        <div className="loom-permissions-modal-title">
          Your verification email was sent!
        </div>
      )}
      {error && <div className="loom-permissions-modal-title">{error}</div>}
    </Fragment>
  );
};

class PermissionModal extends Component {
  state = { showSignup: false, requestSent: false, alreadyPinged: false };

  render() {
    const {
      email,
      error,
      videoId,
      isLoggedIn,
      loggedInChecked,
      isMainVideoOnSharePage,
      isFromSlackDesktopApp,
      userHasEmailVerification,
    } = this.props;

    const { requestSent, alreadyPinged } = this.state;

    if (!loggedInChecked) {
      return null;
    }

    return (
      <div className="loom-video-permission-modal-wrapper">
        <Modal
          show={true}
          showClose={false}
          dark={true}
          className="loom-video-modal"
        >
          {!isLoggedIn && (
            <LoggedOutScreen
              redirect={this.getRedirectString(videoId)}
              isMainVideoOnSharePage={isMainVideoOnSharePage}
              isFromSlackDesktopApp={isFromSlackDesktopApp}
              videoId={videoId}
            />
          )}
          {isLoggedIn && !userHasEmailVerification && (
            <ViewerNeedsEmailVerificationScreen
              email={email}
              error={error}
              onClick={this.sendEmailVerificationEmail}
              requestSent={requestSent}
              isFromSlackDesktopApp={isFromSlackDesktopApp}
            />
          )}
          {isLoggedIn && userHasEmailVerification && (
            <ViewerNotInEmailWhitelistScreen
              error={error}
              onClick={this.pingVideoOwnerForAccess}
              requestSent={requestSent}
              alreadyPinged={alreadyPinged}
              isFromSlackDesktopApp={isFromSlackDesktopApp}
            />
          )}
        </Modal>
      </div>
    );
  }

  getRedirectString = videoId => `${LOOM_URI}/share/${videoId}`;

  showSignupModal = () => this.setState({ showSignup: true });

  pingVideoOwnerForAccess = async () => {
    try {
      this.setState({ error: null });

      const { videoId } = this.props;

      const resp = await fetch(`/v1/videos/${videoId}/access_ping`, {
        credentials: 'include',
        method: 'POST',
      });

      if (resp.status === STATUS_FORBIDDEN) {
        this.setState({ alreadyPinged: true, requestSent: true });

        return;
      } else if (resp.status >= 400) {
        throw new Error(
          `Error checking password - HTTP response: ${resp.status}`
        );
      }

      this.setState({ requestSent: true });
    } catch (err) {
      sentry.logException(err, 'error requesting access for your video');
      this.setState({
        error: 'There was an error requesting access for your video',
      });
    }
  };

  sendEmailVerificationEmail = async () => {
    try {
      this.setState({ error: null });

      const client = getGraphQLClient();
      const { email } = this.props;

      await client.mutate({
        mutation: SendEmailVerificationEmail,
        variables: { email },
      });

      this.setState({ requestSent: true });
    } catch (err) {
      sentry.logException(err, 'error sending email verification email');
      this.setState({
        error: 'There was an error sending your email verification email',
      });
    }
  };
}

const mapStateToProps = state => {
  const {
    current: user,
    is_logged_in: isLoggedIn,
    is_logged_in_checked: loggedInChecked,
  } = state.user;

  const {
    isMainVideoOnSharePage,
    is_from_slack_desktop_app: isFromSlackDesktopApp,
    video: { id: videoId },
  } = state.video_player;

  const userHasEmailVerification = user && user.status === VERIFIED;
  const email = user && user.email;

  return {
    email,
    videoId,
    isLoggedIn,
    loggedInChecked,
    isMainVideoOnSharePage,
    isFromSlackDesktopApp,
    userHasEmailVerification,
  };
};

export default connect(mapStateToProps, null)(PermissionModal);
